import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useStripe, useElements, PaymentElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { ENDPOINTS } from '../config';
import { STRIPE_PUBLISHABLE_KEY } from '../utils/env';
import { getSessionId } from '../utils/analytics';
import { trackEvent } from '../utils/analytics';
import { PaymentMethodIcon } from './CardBrandIcon';
import { Trash2 } from 'lucide-react';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

interface BankDetails {
  bank_name?: string;
  account_type?: string;
  last4?: string;
}

interface BankAccountSetupProps {
  onSetupComplete?: (setupIntentId: string, bankDetails: BankDetails) => void;
  onError?: (error: string) => void;
  invoiceId?: string; // For redirecting back to checkout page
  returnPath?: string; // Alternative return path (e.g. '/wallet') — overrides invoiceId-based URL
  disableRedirect?: boolean; // For modal mode
  existingSetup?: BankAccountSetupIntent | null; // Pass existing setup to avoid duplicate query
  isLoading?: boolean; // Pass loading state to avoid duplicate query
}

interface BankAccountSetupIntent {
  id: string;
  parent_id: string;
  setup_intent_id: string;
  client_secret: string;
  status: string;
  payment_method_id?: string;
  bank_name?: string;
  account_type?: string;
  last4?: string;
  is_active: boolean;
}

interface ParentProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
}

// Loading overlay component for verification process
function VerificationLoadingOverlay() {
  return (
    <div className="fixed inset-0 bg-white bg-opacity-95 z-50 flex items-center justify-center">
      <div className="text-center max-w-md mx-auto p-6">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-6"></div>
        <h3 className="text-xl font-semibold text-gray-900 mb-3">
          Verifying Your Bank Account
        </h3>
        <p className="text-gray-600 mb-4">
          You're being redirected to your bank for secure verification. This may take a few moments.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <p className="text-sm text-blue-700">
            💡 <strong>Tip:</strong> Keep this tab open while you complete verification with your bank.
          </p>
        </div>
      </div>
    </div>
  );
}

// Separate component for the form that needs Elements with clientSecret
function BankAccountSetupForm({
  setupIntentId,
  onComplete,
  onError,
  invoiceId,
  returnPath,
  disableRedirect = false,
  parentProfile
}: {
  setupIntentId: string;
  onComplete: (setupIntentId: string, bankDetails: BankDetails) => void;
  onError: (error: string) => void;
  invoiceId?: string;
  returnPath?: string;
  disableRedirect?: boolean;
  parentProfile?: ParentProfile;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showVerificationOverlay, setShowVerificationOverlay] = useState(false);

  const handleConfirmSetup = async () => {
    if (!stripe || !elements) {
      onError('Setup not ready. Please try again.');
      return;
    }

    setIsProcessing(true);
    setShowVerificationOverlay(true);

    try {
      let result;
      
      if (disableRedirect) {
        result = await stripe.confirmSetup({
          elements,
          redirect: 'if_required'
        });
      } else {
        const returnUrl = returnPath
          ? `${window.location.origin}${returnPath}?bank_setup_complete=true&setup_intent_id=${setupIntentId}`
          : invoiceId
          ? `${window.location.origin}/payment-checkout/${invoiceId}?bank_setup_complete=true&setup_intent_id=${setupIntentId}`
          : `${window.location.origin}/payment-confirmation?setup_intent_id=${setupIntentId}`;
        result = await stripe.confirmSetup({
          elements,
          confirmParams: { return_url: returnUrl },
        });
      }

      setShowVerificationOverlay(false);

      if (result.error) {
        console.error('Setup confirmation error:', result.error);
        onError(result.error.message || 'Failed to verify bank account');
        setIsProcessing(false);
      } else {
        // If no error and we're in non-redirect mode, confirm/refresh status via Edge Function
        if (disableRedirect) {
          try {
            const { data: sessionData } = await supabase.auth.getSession();
            const token = sessionData.session?.access_token;
            if (!token) throw new Error('Not authenticated');

            const res = await fetch(ENDPOINTS.updateBankAccountSetupIntent, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({ setupIntentId: setupIntentId })
            });

            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
              throw new Error(data?.error || 'Failed to refresh setup intent');
            }

            if (data.status === 'succeeded') {
              onComplete(setupIntentId, {});
            } else {
              onError(`Bank verification status: ${data.status || 'unknown'}`);
            }
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to finalize bank verification';
            onError(msg);
          } finally {
            setIsProcessing(false);
          }
        } else {
          // Redirect flow - the update function will be called on return URL
          onComplete(setupIntentId, {});
          setIsProcessing(false);
        }
      }
    } catch (error) {
      console.error('Bank account setup error:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      onError(errorMessage);
      setShowVerificationOverlay(false);
      setIsProcessing(false);
    }
  };

  return (
    <>
      {showVerificationOverlay && <VerificationLoadingOverlay />}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bank Account Details
          </label>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-3">
            <p className="text-sm text-blue-700">
              <strong>📱 What happens next:</strong> After clicking "Verify Bank Account", you'll be redirected to your bank's secure verification system to instantly confirm your account details.
            </p>
          </div>
          <div className="border border-gray-300 rounded-md p-3">
            <PaymentElement
              options={{
                layout: 'tabs',
                defaultValues: parentProfile ? {
                  billingDetails: {
                    email: parentProfile.email,
                    name: `${parentProfile.first_name} ${parentProfile.last_name}`,
                  }
                } : undefined,
              }}
            />
          </div>
        </div>
        
        <button
          onClick={handleConfirmSetup}
          disabled={!stripe || !elements || isProcessing}
          className="w-full py-3 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Connecting to your bank...
            </span>
          ) : 'Verify Bank Account'}
        </button>
      </div>
    </>
  );
}

export function BankAccountSetup({ onSetupComplete, onError, invoiceId, returnPath, disableRedirect, existingSetup: passedExistingSetup, isLoading: passedIsLoading }: BankAccountSetupProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [setupStatus, setSetupStatus] = useState<'idle' | 'setting_up' | 'collecting' | 'verifying' | 'complete'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [currentSetupIntent, setCurrentSetupIntent] = useState<{
    clientSecret: string;
    setupIntentId: string;
  } | null>(null);

  // Query to get parent profile information
  const { data: parentProfile } = useQuery<ParentProfile>({
    queryKey: ['parent-profile', user?.email],
    queryFn: async () => {
      if (!user?.email) throw new Error('No authenticated user');
      
      const { data, error } = await supabase
        .from('parents')
        .select('id, first_name, last_name, email, phone')
        .eq('email', user.email)
        .single();
        
      if (error) throw error;
      if (!data) throw new Error('Parent not found');
      
      return data as ParentProfile;
    },
    enabled: !!user?.email
  });

  // Query to get existing bank account setup (only run if not passed as prop)
  const { data: queriedExistingSetup, isLoading: queriedIsLoading } = useQuery<BankAccountSetupIntent | null>({
    queryKey: ['bank-account-setup', parentProfile?.id],
    queryFn: async () => {
      if (!parentProfile?.id) return null;
      const { data, error } = await supabase
        .from('bank_account_setup_intents')
        .select('*')
        .eq('parent_id', String(parentProfile.id))
        .eq('is_active', true)
        .eq('status', 'succeeded')
        .order('created_at', { ascending: false })
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!parentProfile?.id && passedExistingSetup === undefined && passedIsLoading === undefined // Only query if not passed as prop
  });

  // Use passed props or fallback to query results
  const existingSetup = passedExistingSetup !== undefined ? passedExistingSetup : queriedExistingSetup;
  const isLoading = passedIsLoading !== undefined ? passedIsLoading : queriedIsLoading;

  // Mutation to create setup intent
  const createSetupIntentMutation = useMutation({
    mutationFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        throw new Error('You must be logged in to set up bank account payments.');
      }

      const response = await fetch(ENDPOINTS.createBankAccountSetupIntent, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create bank account setup');
      }

      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bank-account-setup'] });
      setCurrentSetupIntent({
        clientSecret: data.clientSecret,
        setupIntentId: data.setupIntentId
      });
      setSetupStatus('collecting');
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create bank account setup';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  });

  const handleSetupComplete = (setupIntentId: string, bankDetails: BankDetails) => {
    // Track bank account addition event (non-blocking)
    if (user) {
      trackEvent({
        eventType: 'add_payment_method',
        user: user,
        entityType: 'payment_method',
        sessionId: getSessionId(),
        metadata: {
          payment_method: 'ach',
          setup_intent_id: setupIntentId,
          payment_intent: setupIntentId,
          bank_details: bankDetails,
          invoice_id: invoiceId
        },
        email: user.email,
      });
    }

    setSetupStatus('complete');
    queryClient.invalidateQueries({ queryKey: ['bank-account-setup'] });
    onSetupComplete?.(setupIntentId, bankDetails);
  };

  const handleSetupError = (errorMessage: string) => {
    setError(errorMessage);
    setSetupStatus('collecting');
    onError?.(errorMessage);
  };

  const handleStartSetup = () => {
    setError(null);
    setSetupStatus('setting_up');
    createSetupIntentMutation.mutate();
  };

  const handleRemoveSetup = async () => {
    if (!existingSetup) return;

    try {
      const { error } = await supabase
        .from('bank_account_setup_intents')
        .update({ is_active: false })
        .eq('id', existingSetup.id);

      if (error) throw error;

      // Track bank account removal event (non-blocking)
      if (user) {
        trackEvent({
          eventType: 'update_payment_method',
          user: user,
          entityType: 'payment_method',
          sessionId: getSessionId(),
          metadata: {
            payment_method: 'ach',
            setup_intent_id: existingSetup.setup_intent_id,
            action: 'remove',
            bank_details: {
              bank_name: existingSetup.bank_name,
              account_type: existingSetup.account_type,
              last4: existingSetup.last4
            },
            invoice_id: invoiceId
          },
          email: user.email,
        });
      }

      queryClient.invalidateQueries({ queryKey: ['bank-account-setup'] });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove bank account';
      setError(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 border rounded-lg bg-gray-50">
        <p className="text-sm text-gray-600">Loading bank account information...</p>
      </div>
    );
  }

  // Show existing bank account if setup is complete
  if (existingSetup && existingSetup.status === 'succeeded') {
    return (
      <div className="flex items-center justify-between gap-3 px-4 py-3.5 border border-gray-200 rounded-xl bg-white">
        <div className="flex items-center min-w-0">
          <div className="mr-3 shrink-0">
            <PaymentMethodIcon type="bank_account" size={26} />
          </div>
          <div className="min-w-0">
            <p className="text-sm sm:text-base font-medium text-gray-900 truncate">
              {existingSetup.bank_name ?? 'Bank'} •••• {existingSetup.last4 ?? '••••'}
            </p>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-xs sm:text-sm text-gray-500">
              <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                Verified
              </span>
              {existingSetup.account_type && <span>{existingSetup.account_type}</span>}
            </div>
          </div>
        </div>
        <button
          onClick={handleRemoveSetup}
          className="inline-flex items-center gap-1.5 shrink-0 px-2.5 py-1.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Remove
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 border rounded-lg">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Set Up Bank Account Payment</h3>
      
      <div className="space-y-4">
        <div className="bg-blue-50 p-3 rounded-md">
          <p className="text-sm text-blue-700">
            Connect your bank account for secure, low-cost payments. Enter your bank account details below 
            and we'll verify it instantly through your online banking.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 p-3 rounded-md">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {setupStatus === 'idle' && (
          <button
            onClick={handleStartSetup}
            disabled={createSetupIntentMutation.isPending}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Connect Bank Account
          </button>
        )}

        {setupStatus === 'setting_up' && (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-600 mt-2">Setting up bank account verification...</p>
          </div>
        )}

        {setupStatus === 'collecting' && currentSetupIntent && (
          <Elements 
            stripe={stripePromise} 
            options={{
              clientSecret: currentSetupIntent.clientSecret,
              appearance: {
                theme: 'stripe',
              },
            }}
          >
            <BankAccountSetupForm
              setupIntentId={currentSetupIntent.setupIntentId}
              onComplete={handleSetupComplete}
              onError={handleSetupError}
              invoiceId={invoiceId}
              returnPath={returnPath}
              disableRedirect={disableRedirect}
              parentProfile={parentProfile}
            />
          </Elements>
        )}

        {setupStatus === 'verifying' && (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-600 mt-2">Verifying your bank account...</p>
          </div>
        )}

        {setupStatus === 'complete' && (
          <div className="bg-green-50 p-3 rounded-md">
            <p className="text-sm text-green-700">
              ✓ Bank account successfully connected! You can now use it for payments.
            </p>
          </div>
        )}
      </div>
    </div>
  );
} 